/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Component, Host, h, Prop, Watch, State, Event, type EventEmitter, Element, Method} from '@stencil/core';
import type { FacetChildUiModel, FacetTreeUiModel } from '../../../../models/ui/facets/facet-tree-ui.model';
import { cloneObjectWithNoReference } from '../../../../utils/helpers/clone-object-with-no-reference';
import type { FilterNode } from '../../../result-page/facets-build-wrapper/filter-node';
import {FilterChangeContext} from '../../../result-page/facets-build-wrapper/models/filter-change-context.enum';
import type { labelProviderInterface } from '../../../LabelProviderInterface';

@Component({
  tag: 'filters-tree-menu',
  styleUrl: 'filters-tree-menu.scss',
  shadow: false,
  scoped: true
})
export class FiltersTreeMenu {
  @Element() el!: HTMLElement;

  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly rtl: boolean;
  @Prop() readonly hiddenFacets = '[]';
  @Prop({ attribute: 'search-filter-label' }) readonly searchFilterLabel: string;
  @Prop({attribute: 'filters-tree'}) readonly filtersTreeProp: string = JSON.stringify([]);
  @Prop({attribute: 'outer-filters-tree'}) readonly outerFiltersTreeProp: string = JSON.stringify([]);
  @Prop({attribute: 'filters-header-height', mutable: false}) readonly filtersHeaderHeight? : number;
  @Prop({attribute: 'filters-reset-height', mutable: false}) readonly filtersResetHeight? : number;
  @Prop({attribute: 'filters-result-button-height', mutable: false}) readonly filtersResultButtonHeight? : number;
  @Prop({attribute: 'max-filter-height'}) readonly maxFilterHeight: number;
  @Prop({attribute: 'show-filters-search-input-limit'}) readonly showFiltersSearchInputLimit: number | null;
  @Prop() readonly filterByLocalChipsState: boolean = false;
  @Prop() readonly forceCollapse: boolean = false;
  @Prop({attribute: 'facet-local-chips'}) readonly facetLocalChipsProp: FilterNode[] = [];
  @Watch('facetLocalChipsProp')
  onFacetLocalChips(newValue: FilterNode[]): void {
      this.facetLocalChipsState = newValue;
  }

  @State() facetLocalChipsState: FilterNode[] = [];

  private filtersTree: FacetTreeUiModel[] = [];
  private outerFiltersTree: FacetTreeUiModel[] = [];
  private hiddenFacetsArray: string[] = [];

  @Event({bubbles: false}) filterChange: EventEmitter<FilterNode>;
  @Event({eventName: 'changeFiltersMobile', bubbles: true}) changeFiltersMobile: EventEmitter<{filterNodes: FilterNode[], context: FilterChangeContext}>;

  @Method()
  async clearSelected() {
      // @ts-ignore
      for(const element of this.el.querySelectorAll('filters-tree-node')) {
        (element as any).clearSelected();
      }
  }

  private onCollapse(event: CustomEvent) {
      const {element, collapse, filtersCount} = event.detail;

      if(collapse) {
      // @ts-ignore
        [...this.el.querySelectorAll('filters-tree-node')].forEach((el) => {
            if (element !== el && (element.level === el.level)) {
              el.collapseClose();
            }
        });
      }

      if(collapse) {
        setTimeout(() => {
            const expandFiltersHeight = (filtersCount > 15 ? 15 : filtersCount) * this.maxFilterHeight;
            const filtersSpaceHeight = window.innerHeight - (this.filtersHeaderHeight - ((this.el.classList.contains('has-selected') ? this.filtersResetHeight : 0))) - this.filtersResultButtonHeight;
            const scrollToTopValue = element.offsetTop - this.filtersHeaderHeight - (this.el.classList.contains('has-selected') ? this.filtersResetHeight : 0);
            const doScroll = ((window.innerHeight - this.filtersResultButtonHeight) - (element as HTMLElement).getBoundingClientRect().bottom) <= 0;

            if (doScroll) {
              this.el.scrollTo({
                top: (expandFiltersHeight >= filtersSpaceHeight) ? scrollToTopValue : element.offsetTop - (filtersSpaceHeight - expandFiltersHeight),
                left: 0,
                behavior: 'smooth'
              });
            }
        }, 400);
      }
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            return fallback;
        };
      }
      this.filtersTree = JSON.parse(this.filtersTreeProp);
      this.outerFiltersTree = JSON.parse(this.outerFiltersTreeProp);
      this.hiddenFacetsArray = JSON.parse(this.hiddenFacets);
      this.facetLocalChipsState = this.facetLocalChipsProp;
  }

  componentWillUpdate() {
      this.filtersTree = JSON.parse(this.filtersTreeProp);
      this.outerFiltersTree = JSON.parse(this.outerFiltersTreeProp);
      this.hiddenFacetsArray = JSON.parse(this.hiddenFacets);
  }

  render() {
      return (
        <Host>
          {this.filtersTree ? this.renderNode(cloneObjectWithNoReference<FacetTreeUiModel[]>(this.filtersTree as FacetTreeUiModel[]), true, 0) : ''}
          {(this.outerFiltersTree && this.outerFiltersTree?.length > 0)
            ? this.renderOuterNode(cloneObjectWithNoReference<FacetTreeUiModel[]>(this.outerFiltersTree), true, 0)
            : ''}
        </Host>
      );
  }

  private renderNode(children: FacetTreeUiModel[] | FacetChildUiModel[], isRoot = true, level) {
      level++;
      return (children.map((f, index) => (
        (!this.hiddenFacetsArray.includes(f['id']) && (f.children?.length > 0 || isRoot)) ?
          <div class={'ftm-menu-element'} key={index + f.count}>
            {(f.expression === 'SINGLE')
              ? <filters-single-facet-node
                filterNode={f}
                forceCollapse={this.forceCollapse}
                labelProvider={this.labelProvider}
                onChanged={(e: CustomEvent) => {
                    this.changeFiltersMobile.emit({filterNodes: e.detail.children, context: FilterChangeContext.mobile});
                }}
              >
              </filters-single-facet-node>
              : <filters-tree-node
                treeNodeProp={JSON.stringify(f)}
                isRootProp={`${isRoot}`}
                level={level}
                forceCollapse={this.forceCollapse}
                showInputProp={(this.showFiltersSearchInputLimit && f.children.length >= this.showFiltersSearchInputLimit) ? 'true' : 'false'}
                onFilterChange={(e) => this.filterChange.emit(e.detail)}
                onCollapse={(e) => this.onCollapse(e)}
                searchFilterLabel={this.searchFilterLabel}
                labelProvider={this.labelProvider as labelProviderInterface}
                rtl={this.rtl}
                facetLocalChipsProp={this.facetLocalChipsState}
                filterByLocalChipsState={this.filterByLocalChipsState}
              >
                {f?.children && f?.children?.length > 0 && (this.renderNode(cloneObjectWithNoReference<FacetChildUiModel[]>(f.children), false, level))}
              </filters-tree-node>}
          </div>
          : ''
      )));
  }

  private renderOuterNode(children: FacetTreeUiModel[] | FacetChildUiModel[], isRoot = true, level) {
      return (children.map((f, index) => (
        (!this.hiddenFacets.includes(f['id']) && (f.children?.length > 0 || isRoot)) ?
          <div>
            <div class={'ftm-menu-element'}>
              {f?.children && f?.children?.length > 0 && (this.renderNode(cloneObjectWithNoReference<FacetChildUiModel[]>(f.children), false, 0))}
            </div>
          </div>
          : ''
      )));
  }
}
